/**
 * RecurringInvoiceCreateModal — create-template dialog for the
 * Recurring Invoices page. Extracted from `RecurringInvoices.jsx`
 * to keep the parent component small and focused on listing/state.
 *
 * Props
 * -----
 * open          — boolean, controls visibility
 * onClose       — () => void, dismiss handler
 * onSaved       — (template) => void, fired after a successful POST
 * customers     — array of {id, name} for the dropdown; empty → freetext input
 * workspaceId   — string, current workspace / sync key
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Eye, Plus, Trash2, X } from 'lucide-react';
import { fetchWithAuth } from '../../services/TokenRefresh';
import InvoicePreviewModal from '../invoices/InvoicePreviewModal';
import { CLEANING_SERVICES } from '../../utils/invoiceHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CADENCES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

// Timezones — try the browser's full IANA list first (Chrome 99+/FF 93+
// covers ~440 zones) and fall back to a curated worldwide list on older
// runtimes. The curated list is grouped by region so the dropdown stays
// scannable.
const TIMEZONE_FALLBACK = [
  // Americas
  'America/Anchorage', 'America/Los_Angeles', 'America/Phoenix', 'America/Denver',
  'America/Chicago', 'America/Mexico_City', 'America/New_York', 'America/Toronto',
  'America/Halifax', 'America/St_Johns', 'America/Sao_Paulo', 'America/Argentina/Buenos_Aires',
  'America/Bogota', 'America/Lima', 'America/Santiago', 'America/Caracas',
  'Pacific/Honolulu', 'Pacific/Midway', 'Pacific/Pago_Pago',
  // Atlantic / Europe
  'Atlantic/Azores', 'Atlantic/Cape_Verde', 'Atlantic/Reykjavik',
  'Europe/London', 'Europe/Dublin', 'Europe/Lisbon', 'Europe/Paris', 'Europe/Madrid',
  'Europe/Berlin', 'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Rome', 'Europe/Zurich',
  'Europe/Vienna', 'Europe/Warsaw', 'Europe/Prague', 'Europe/Stockholm', 'Europe/Oslo',
  'Europe/Copenhagen', 'Europe/Helsinki', 'Europe/Athens', 'Europe/Istanbul',
  'Europe/Moscow', 'Europe/Kyiv', 'Europe/Bucharest',
  // Africa
  'Africa/Casablanca', 'Africa/Lagos', 'Africa/Cairo', 'Africa/Johannesburg',
  'Africa/Nairobi', 'Africa/Algiers', 'Africa/Tunis',
  // Middle East
  'Asia/Jerusalem', 'Asia/Beirut', 'Asia/Riyadh', 'Asia/Dubai', 'Asia/Tehran',
  // Asia
  'Asia/Karachi', 'Asia/Kolkata', 'Asia/Colombo', 'Asia/Kathmandu', 'Asia/Dhaka',
  'Asia/Yangon', 'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Ho_Chi_Minh', 'Asia/Kuala_Lumpur',
  'Asia/Singapore', 'Asia/Manila', 'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Taipei',
  'Asia/Seoul', 'Asia/Tokyo',
  // Oceania
  'Australia/Perth', 'Australia/Adelaide', 'Australia/Darwin', 'Australia/Brisbane',
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Hobart',
  'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Guam',
  // UTC
  'UTC',
];

let ALL_TIMEZONES;
try {
  ALL_TIMEZONES = (typeof Intl !== 'undefined' && Intl.supportedValuesOf)
    ? Intl.supportedValuesOf('timeZone')
    : TIMEZONE_FALLBACK;
  if (!Array.isArray(ALL_TIMEZONES) || ALL_TIMEZONES.length === 0) {
    ALL_TIMEZONES = TIMEZONE_FALLBACK;
  }
} catch (_) {
  ALL_TIMEZONES = TIMEZONE_FALLBACK;
}

// Group IANA zones by their region prefix so the dropdown is readable.
const TIMEZONE_GROUPS = (() => {
  const groups = new Map();
  ALL_TIMEZONES.forEach((tz) => {
    const region = tz.includes('/') ? tz.split('/')[0] : 'Other';
    if (!groups.has(region)) groups.set(region, []);
    groups.get(region).push(tz);
  });
  // Order groups so the most common regions surface first, remaining alphabetically.
  const priority = ['America', 'Europe', 'Africa', 'Asia', 'Australia', 'Pacific', 'Atlantic', 'Indian', 'Antarctica', 'Arctic', 'UTC', 'Etc', 'Other'];
  const ordered = [];
  priority.forEach((p) => {
    if (groups.has(p)) {
      ordered.push([p, groups.get(p).sort()]);
      groups.delete(p);
    }
  });
  Array.from(groups.entries()).sort().forEach((entry) => ordered.push([entry[0], entry[1].sort()]));
  return ordered;
})();

const PAYMENT_DUE_OPTIONS = [
  { value: 0,  label: 'On Receipt' },
  { value: 7,  label: 'Within 7 Days' },
  { value: 14, label: 'Within 14 Days' },
  { value: 30, label: 'Within 30 Days' },
  { value: 45, label: 'Within 45 Days' },
  { value: 60, label: 'Within 60 Days' },
  { value: 90, label: 'Within 90 Days' },
];

const paymentDueLabel = (d) =>
  (PAYMENT_DUE_OPTIONS.find((o) => o.value === Number(d)) || PAYMENT_DUE_OPTIONS[3]).label;

const DAY_OF_MONTH_OPTIONS = [
  { value: 1, label: 'First' },
  { value: -1, label: 'Last' },
  ...Array.from({ length: 27 }, (_, i) => {
    const n = i + 2; // 2..28
    const suf = n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
    return { value: n, label: `${n}${suf}` };
  }),
];

// Format a Date as YYYY-MM-DD in local time (avoid the UTC-off-by-one bug
// that toISOString() causes for late-night dates in western timezones).
const localDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

function authHeaders() {
  // Only content-type — auth header is added by fetchWithAuth (memory JWT +
  // auto-refresh via httpOnly cookie).
  return { 'Content-Type': 'application/json' };
}

// Read the workspace-wide default tax rate saved on the Settings screen.
// Falls back to 0 when not set. Same fields Invoices / Estimates use so all
// three surfaces stay in sync.
const readDefaultTaxRate = () => {
  try {
    const s = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
    const tr = Number(s.taxRate ?? s.salesTaxRate);
    return Number.isFinite(tr) ? tr : 0;
  } catch (_) {
    return 0;
  }
};

const blankForm = () => {
  const today = new Date();
  const guessedTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  return {
    customer_id: '', customer_name: '',
    items: [],
    // Seed from workspace Settings so a fresh recurring template inherits
    // the same tax rate the user configured once for invoices.
    tax_rate: readDefaultTaxRate(),
    discount: 0, deposit_amount: 0,
    notes: '', terms: 'Payment due within 15 days',
    cadence: 'monthly',
    // Schedule
    start_date: localDate(today),
    day_of_month: 1,
    end_mode: 'never',
    end_date: '',
    end_after_count: 12,
    timezone: guessedTz,
    payment_due_days: 30,
  };
};

export default function RecurringInvoiceCreateModal({
  open, onClose, onSaved, customers, workspaceId, prefill,
}) {
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({});

  useEffect(() => {
    if (!open) return;
    // Base blank template, then overlay prefill (from Invoices → Make Recurring)
    const base = blankForm();
    if (prefill && typeof prefill === 'object') {
      setForm({
        ...base,
        customer_id: prefill.customerId || '',
        customer_name: prefill.customerName || '',
        customer_address: prefill.customerAddress || '',
        customer_email: prefill.customerEmail || '',
        customer_phone: prefill.customerPhone || '',
        items: Array.isArray(prefill.items) && prefill.items.length ? prefill.items.map((it, i) => ({
          id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `p-${i}`,
          description: it.description || '',
          quantity: Number(it.quantity) || 1,
          rate: Number(it.rate) || 0,
          amount: Number(it.amount || (Number(it.quantity) || 1) * (Number(it.rate) || 0)),
        })) : base.items,
        tax_rate: Number.isFinite(Number(prefill.tax_rate)) ? Number(prefill.tax_rate) : base.tax_rate,
        discount: Number(prefill.discount) || 0,
        deposit_amount: Number(prefill.deposit_amount) || 0,
        notes: prefill.notes || base.notes,
        terms: prefill.terms || base.terms,
      });
    } else {
      setForm(base);
    }
  }, [open, prefill]);

  // Pull company info + default tax rate from Settings so the preview and
  // the seeded tax stay in sync with the workspace-wide config.
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem('maidly_settings');
      if (raw) {
        const s = JSON.parse(raw);
        if (s?.companyInfo) setCompanyInfo(s.companyInfo);
        const tr = Number(s.taxRate ?? s.salesTaxRate);
        if (Number.isFinite(tr)) {
          setForm((f) => ({ ...f, tax_rate: tr }));
        }
      }
    } catch (_) { /* ignore */ }
  }, [open]);

  // Also refresh if the user updates Settings while the modal is open in
  // another tab. Settings.js dispatches `maidly:settings-updated` on save.
  useEffect(() => {
    const refresh = () => {
      try {
        const s = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
        if (s?.companyInfo) setCompanyInfo(s.companyInfo);
        const tr = Number(s.taxRate ?? s.salesTaxRate);
        if (Number.isFinite(tr)) {
          setForm((f) => ({ ...f, tax_rate: tr }));
        }
      } catch (_) { /* ignore */ }
    };
    window.addEventListener('maidly:settings-updated', refresh);
    return () => window.removeEventListener('maidly:settings-updated', refresh);
  }, []);

  // The picked customer, used to render the read-only Bill To preview below
  // the dropdown. Falls back to `null` when nothing is picked yet.
  const selectedCustomer = customers.find((c) => c.id === form.customer_id) || null;

  // Build an invoice-shaped object from the current form state so the
  // InvoicePreviewModal (shared with real invoices) can render exactly what
  // a generated draft will look like. Payment terms + due date reflect the
  // Payment Due dropdown so users can double-check before saving.
  const previewInvoice = useMemo(() => {
    const days = Number(form.payment_due_days ?? 30);
    const start = form.start_date ? new Date(form.start_date + 'T00:00:00') : new Date();
    const due = new Date(start.getTime());
    due.setDate(due.getDate() + days);
    const items = (form.items || []).map((it, idx) => ({
      id: `preview-${idx}`,
      description: it.description || '',
      quantity: Number(it.quantity || 1),
      rate: Number(it.rate || 0),
      amount: Number(it.amount || Number(it.quantity || 1) * Number(it.rate || 0)),
    }));
    const subtotal = items.reduce((s, it) => s + Number(it.amount || 0), 0);
    const discount = Number(form.discount || 0);
    const taxed = Math.max(0, subtotal - discount);
    const tax = (taxed * Number(form.tax_rate || 0)) / 100;
    const totalCalc = taxed + tax - Number(form.deposit_amount || 0);
    const dueLabel = paymentDueLabel(days);
    return {
      id: 'preview',
      invoiceNumber: 'PREVIEW',
      customerId: form.customer_id || '',
      customerName: form.customer_name || selectedCustomer?.name || '',
      customerAddress: form.customer_address || selectedCustomer?.address || '',
      customerEmail: form.customer_email || selectedCustomer?.email || '',
      customerPhone: form.customer_phone || selectedCustomer?.phone || '',
      items,
      subtotal,
      discount,
      taxRate: Number(form.tax_rate || 0),
      tax,
      depositAmount: Number(form.deposit_amount || 0),
      total: totalCalc,
      status: 'pending',
      date: start.toISOString().slice(0, 10),
      dueDate: due.toISOString().slice(0, 10),
      notes: form.notes || '',
      terms: `Payment due ${dueLabel.toLowerCase()}.`,
      paymentDueDays: days,
      generatedFromRecurring: true,
    };
  }, [form, selectedCustomer]);

  if (!open) return null;

  const onCustomer = (e) => {
    const cid = e.target.value;
    const cust = customers.find((c) => c.id === cid);
    // Store the whole picked customer so the modal can render a read-only
    // Bill To summary and so any downstream generator has the address/etc.
    setForm((f) => ({
      ...f,
      customer_id: cid,
      customer_name: cust?.name || '',
      customer_address: cust?.address || '',
      customer_email: cust?.email || '',
      customer_phone: cust?.phone || '',
    }));
  };

  // ---- Line items ---------------------------------------------------------
  // Users define what will be billed on every recurring cycle. Kept intentionally
  // compact: one row per line, quick-pick from CLEANING_SERVICES, add / remove.
  const blankItem = () => ({
    id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()),
    description: '', quantity: 1, rate: 0, amount: 0,
  });
  const addItem = () => setForm((f) => ({ ...f, items: [...(f.items || []), blankItem()] }));
  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: (f.items || []).filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, value) => {
    setForm((f) => {
      const items = (f.items || []).map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, [field]: value };
        if (field === 'quantity' || field === 'rate') {
          next.amount = Number(field === 'quantity' ? value : it.quantity) *
                        Number(field === 'rate' ? value : it.rate);
        }
        return next;
      });
      return { ...f, items };
    });
  };
  const addServiceItem = (svcDesc) => {
    const svc = CLEANING_SERVICES.find((s) => s.description === svcDesc);
    if (!svc) return;
    const it = { ...blankItem(), description: svc.description, rate: svc.rate, amount: svc.rate };
    setForm((f) => ({ ...f, items: [...(f.items || []).filter((x) => x.description), it] }));
  };

  const submit = async () => {
    if (!form.customer_id) {
      toast.error('Please pick a customer from the list');
      return;
    }
    setSaving(true);
    try {
      // Only pass end_date when the user picked "By date". After-N-times is
      // stored as end_mode + end_after_count for the future scheduler to
      // enforce (current scheduler still runs off cadence + start).
      const payload = {
        workspace_id: workspaceId,
        customer_id: form.customer_id,
        customer_name: form.customer_name,
        items: form.items,
        tax_rate: form.tax_rate,
        discount: form.discount,
        deposit_amount: form.deposit_amount,
        notes: form.notes,
        terms: form.terms,
        cadence: form.cadence,
        start_date: form.start_date || null,
        end_date: form.end_mode === 'by_date' && form.end_date ? form.end_date : null,
        day_of_month: form.cadence === 'monthly' ? form.day_of_month : null,
        timezone: form.timezone || null,
        end_mode: form.end_mode,
        end_after_count: form.end_mode === 'after_count' ? Number(form.end_after_count) : null,
        payment_due_days: Number(form.payment_due_days ?? 30),
      };
      const res = await fetchWithAuth(`${API_URL}/api/recurring-invoices`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.detail || 'Failed to create template');
      }
      toast.success('Recurring schedule created');
      // Broadcast so any other tab/view listening can refresh its list too.
      try {
        window.dispatchEvent(new CustomEvent('maidly:recurring-updated', { detail: { action: 'create' } }));
      } catch (_) { /* ignore */ }
      onSaved(data.template);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="recurring-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 16,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: 'min(720px, 100%)',
          maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
              Recurring invoices
            </h2>
          </div>
          <button
            data-testid="recurring-modal-close"
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#64748b', padding: 6,
            }}
          ><X size={20} /></button>
        </div>

        <div style={{ padding: 22, display: 'grid', gap: 16 }}>
          {/* Customer — always dropdown-only. Free-text create was removed
              so recurring templates always link to a real customer record;
              add new customers from the Customers tab. */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Customer</label>
            {customers.length > 0 ? (
              <select
                data-testid="recurring-customer-select"
                value={form.customer_id}
                onChange={onCustomer}
                style={{
                  width: '100%', padding: '10px 12px', marginTop: 6,
                  border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 14,
                }}
              >
                <option value="">— Select customer —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            ) : (
              <div
                data-testid="recurring-no-customers"
                style={{
                  marginTop: 6, padding: '10px 12px',
                  border: '1px dashed #fcd34d', background: '#fffbeb',
                  borderRadius: 10, fontSize: 13, color: '#92400e',
                }}
              >
                No customers on file yet. Open the <strong>Customers</strong> tab to add one, then come back here to set up a recurring schedule.
              </div>
            )}

            {/* Bill To preview intentionally removed — the customer's info
                is still stored on the template and will populate the
                generated invoice's BILL TO block. */}
          </div>

          {/* Line items — what will be billed on every recurring cycle. */}
          <div
            data-testid="recurring-items-card"
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 14,
              padding: 18,
              background: '#fff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%', border: '2px solid #0073ea',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14, color: '#0073ea',
              }}>1</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Line items</div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <select
                  data-testid="recurring-quick-add-service"
                  onChange={(e) => {
                    if (e.target.value) { addServiceItem(e.target.value); e.target.value = ''; }
                  }}
                  style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12, background: '#fff' }}
                >
                  <option value="">Quick add service…</option>
                  {CLEANING_SERVICES.map((s) => (
                    <option key={s.description} value={s.description}>{s.description}</option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid="recurring-add-item"
                  onClick={addItem}
                  style={{
                    background: '#0073ea', color: '#fff', border: 'none', padding: '6px 12px',
                    borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                ><Plus size={14} /> Add item</button>
              </div>
            </div>

            {(form.items || []).length === 0 ? (
              <div style={{ fontSize: 13, color: '#94a3b8', padding: '8px 2px' }}>
                No items yet. Use <strong>Add item</strong> or the quick-add dropdown to build the invoice that repeats each cycle.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: '3fr 0.7fr 1fr 1fr 32px',
                  gap: 8, fontSize: 11, fontWeight: 700, color: '#64748b',
                  textTransform: 'uppercase', letterSpacing: 0.4, padding: '0 2px',
                }}>
                  <div>Description</div>
                  <div style={{ textAlign: 'center' }}>Qty</div>
                  <div style={{ textAlign: 'right' }}>Rate</div>
                  <div style={{ textAlign: 'right' }}>Amount</div>
                  <div />
                </div>
                {(form.items || []).map((it, idx) => (
                  <div
                    key={it.id || idx}
                    data-testid={`recurring-item-row-${idx}`}
                    style={{ display: 'grid', gridTemplateColumns: '3fr 0.7fr 1fr 1fr 32px', gap: 8, alignItems: 'center' }}
                  >
                    <input
                      type="text"
                      placeholder="Service description"
                      value={it.description}
                      onChange={(e) => updateItem(idx, 'description', e.target.value)}
                      style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
                    />
                    <input
                      type="number"
                      min="0"
                      value={it.quantity}
                      onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                      style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, textAlign: 'center' }}
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.rate}
                      onChange={(e) => updateItem(idx, 'rate', e.target.value)}
                      style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, textAlign: 'right' }}
                    />
                    <div style={{ padding: '8px 10px', fontSize: 13, fontWeight: 600, textAlign: 'right', color: '#0f172a' }}>
                      ${Number(it.amount || 0).toFixed(2)}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      data-testid={`recurring-remove-item-${idx}`}
                      style={{
                        background: 'transparent', border: '1px solid #fecaca', color: '#dc2626',
                        borderRadius: 8, padding: 6, cursor: 'pointer',
                      }}
                    ><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Tax / Discount / Deposit compact row */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 10, marginTop: 14, paddingTop: 12, borderTop: '1px dashed #e5e7eb',
            }}>
              <label style={{ display: 'block' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Tax rate (%)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.tax_rate}
                  onChange={(e) => setForm((f) => ({ ...f, tax_rate: Number(e.target.value) }))}
                  style={{ width: '100%', marginTop: 6, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
                />
              </label>
              <label style={{ display: 'block' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Discount ($)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.discount}
                  onChange={(e) => setForm((f) => ({ ...f, discount: Number(e.target.value) }))}
                  style={{ width: '100%', marginTop: 6, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
                />
              </label>
              <label style={{ display: 'block' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Deposit ($)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.deposit_amount}
                  onChange={(e) => setForm((f) => ({ ...f, deposit_amount: Number(e.target.value) }))}
                  style={{ width: '100%', marginTop: 6, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
                />
              </label>
            </div>

            {/* Live total */}
            <div
              data-testid="recurring-total-display"
              style={{
                marginTop: 12, textAlign: 'right', fontSize: 13, color: '#0f172a',
              }}
            >
              <strong>Total per cycle: </strong>
              <span style={{ fontSize: 15, fontWeight: 800 }}>
                ${Number(previewInvoice.total || 0).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Set schedule — new design with cadence dropdown, day-of-month
              picker, first-invoice date + end mode, and time zone. */}
          <div
            data-testid="recurring-schedule-card"
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 14,
              padding: 18,
              background: '#fff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%', border: '2px solid #0073ea',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14, color: '#0073ea',
              }}>2</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Set schedule</div>
            </div>

            {/* Row 1: Repeat this invoice [cadence] (on the [day] day of every month) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#334155', minWidth: 140 }}>Repeat this invoice</label>
              <select
                data-testid="recurring-cadence-select"
                value={form.cadence}
                onChange={(e) => setForm((f) => ({ ...f, cadence: e.target.value }))}
                style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 13, background: '#fff', minWidth: 140 }}
              >
                {CADENCES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              {form.cadence === 'monthly' && (
                <>
                  <span style={{ fontSize: 13, color: '#334155' }}>on the</span>
                  <select
                    data-testid="recurring-day-of-month"
                    value={form.day_of_month}
                    onChange={(e) => setForm((f) => ({ ...f, day_of_month: Number(e.target.value) }))}
                    style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 13, background: '#fff', minWidth: 100 }}
                  >
                    {DAY_OF_MONTH_OPTIONS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: 13, color: '#334155' }}>day of every month</span>
                </>
              )}
            </div>

            {/* Row 2: Invoice date + end [never|date|count] */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#334155', minWidth: 140 }}>Invoice date</label>
              <input
                type="date"
                data-testid="recurring-start-date"
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 13, background: '#fff' }}
              />
              <span style={{ fontSize: 13, color: '#334155' }}>and end</span>
              <select
                data-testid="recurring-end-mode"
                value={form.end_mode}
                onChange={(e) => setForm((f) => ({ ...f, end_mode: e.target.value }))}
                style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 13, background: '#fff', minWidth: 110 }}
              >
                <option value="never">Never</option>
                <option value="by_date">By date</option>
                <option value="after_count">After…</option>
              </select>
              {form.end_mode === 'by_date' && (
                <input
                  type="date"
                  data-testid="recurring-end-date"
                  value={form.end_date}
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                  style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 13, background: '#fff' }}
                />
              )}
              {form.end_mode === 'after_count' && (
                <>
                  <input
                    type="number"
                    min="1"
                    max="999"
                    data-testid="recurring-end-count"
                    value={form.end_after_count}
                    onChange={(e) => setForm((f) => ({ ...f, end_after_count: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 13, background: '#fff', width: 90 }}
                  />
                  <span style={{ fontSize: 13, color: '#334155' }}>invoices</span>
                </>
              )}
            </div>

            {/* Quick-pick shortcuts for start_date */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginLeft: 150, marginBottom: 14 }}>
              <button
                type="button"
                data-testid="recurring-shortcut-last-day"
                onClick={() => {
                  const t = new Date();
                  // Last day of current month = day 0 of next month
                  const lastDay = new Date(t.getFullYear(), t.getMonth() + 1, 0);
                  setForm((f) => ({ ...f, start_date: localDate(lastDay) }));
                }}
                style={{ background: 'transparent', border: 'none', color: '#0073ea', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
              >Last day of this month</button>
              <span style={{ color: '#cbd5e1' }}>·</span>
              <button
                type="button"
                data-testid="recurring-shortcut-first-day"
                onClick={() => {
                  const t = new Date();
                  const firstNext = new Date(t.getFullYear(), t.getMonth() + 1, 1);
                  setForm((f) => ({ ...f, start_date: localDate(firstNext) }));
                }}
                style={{ background: 'transparent', border: 'none', color: '#0073ea', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
              >First day of next month</button>
            </div>

            {/* Row 3: Create in [timezone] time zone */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <label style={{ fontSize: 13, color: '#334155', minWidth: 140 }}>Create in</label>
              <select
                data-testid="recurring-timezone"
                value={form.timezone}
                onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
                style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 13, background: '#fff', minWidth: 260 }}
              >
                {TIMEZONE_GROUPS.map(([region, zones]) => (
                  <optgroup key={region} label={region}>
                    {zones.map((tz) => (
                      <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <span style={{ fontSize: 13, color: '#334155' }}>time zone</span>
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginLeft: 150 }}>
              Set a time zone to ensure invoice delivery in the morning based on the recipient&apos;s time zone.
            </div>

            {/* Row 4: Payment Due — how soon after the invoice is generated
                the customer is expected to pay. Persists as payment_due_days
                on the template and is applied to each generated draft. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 14 }}>
              <label style={{ fontSize: 13, color: '#334155', minWidth: 140 }}>Payment Due</label>
              <select
                data-testid="recurring-payment-due"
                value={form.payment_due_days}
                onChange={(e) => setForm((f) => ({ ...f, payment_due_days: Number(e.target.value) }))}
                style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 13, background: '#fff', minWidth: 180 }}
              >
                {PAYMENT_DUE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Items / Totals / Notes intentionally removed — this modal
              focuses on the schedule. Add invoice content on each generated
              draft. */}
        </div>

        <div style={{
          padding: 18, borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
        }}>
          <button
            type="button"
            data-testid="recurring-preview-btn"
            onClick={() => setPreviewOpen(true)}
            style={{
              background: '#fff', border: '1px solid #cbd5e1', color: '#0f172a',
              padding: '10px 18px', borderRadius: 10, fontWeight: 600, fontSize: 14,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          ><Eye size={16} /> Preview invoice</button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              style={{
                background: '#fff', border: '1px solid #e5e7eb', padding: '10px 18px',
                borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >Cancel</button>
            <button
              data-testid="recurring-submit"
              onClick={submit}
              disabled={saving}
              style={{
                background: '#0073ea', color: '#fff', border: 'none', padding: '10px 18px',
                borderRadius: 10, fontWeight: 600, fontSize: 14,
                cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1,
              }}
            >{saving ? 'Saving…' : 'Create schedule'}</button>
          </div>
        </div>
      </motion.div>

      {/* Preview modal — reuses the exact InvoicePreviewModal used for real
          invoices so the user sees the actual layout / stamps / footer. */}
      <InvoicePreviewModal
        open={previewOpen}
        previewInvoice={previewInvoice}
        companyInfo={companyInfo}
        companySignature=""
        printRef={null}
        onClose={() => setPreviewOpen(false)}
        onDownload={() => {
          toast.info('Save the schedule to enable PDF download for generated drafts.');
        }}
        onRequestCustomerSignature={() => {}}
      />
    </div>
  );
}
